import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { supabase } from '@/lib/supabase'
import type { Message, Project, Ticket, UserProfile } from '@/lib/types'
import { cn, formatRelative, profileName } from '@/lib/utils'
import { useNotify } from './ConfirmDialog'
import {
  X,
  Loader2,
  Sparkles,
  Copy,
  Check,
  Send,
  MessageCircle,
  Clock,
  Lock,
} from 'lucide-react'

interface Props {
  /** The ticket to reply to. Null = closed. */
  ticket: Ticket | null
  /** Project that owns the ticket — drives permission + the recipient. */
  project: Project | null
  /** Current msgban user (for permission + bubble alignment). */
  currentUserId: string
  /** Subscribers of this ticket (null = unknown / still loading). */
  subscriberIds: Set<string> | null
  /** Owner + members of the project (used for the 'all_members' permission). */
  subscribableUserIds: string[]
  /** Profiles for author names / avatars in the thread. */
  profilesById: Record<string, UserProfile>
  onClose: () => void
  /** Optimistic upstream hook fired with the freshly-sent outbound message. */
  onReplySent?: (msg: Message) => void
}

const WINDOW_MS = 24 * 60 * 60 * 1000

/** « Répondre » dialog — reply to the WhatsApp contact from a card.
 *  Shows the card's conversation thread (inbound/outbound bubbles),
 *  3 AI-suggested replies, and a composer. Sending relays the text to
 *  the `d360-send` edge function (real WhatsApp send via 360dialog),
 *  persists the outbound message and attaches it to the card.
 *
 *  Two guard rails gate the send button:
 *   - permission: owner / assignee always; others per project.reply_permission
 *   - 24h window: WhatsApp forbids free-form messages > 24h after the
 *     customer's last inbound message (no template support in V1).
 */
export function ReplyDialog({
  ticket,
  project,
  currentUserId,
  subscriberIds,
  subscribableUserIds,
  profilesById,
  onClose,
  onReplySent,
}: Props) {
  const { t } = useTranslation()
  const notify = useNotify()
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null)
  const [thread, setThread] = useState<Message[]>([])
  const [lastInboundAt, setLastInboundAt] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const composerRef = useRef<HTMLTextAreaElement | null>(null)

  // AI suggestions can be turned off per channel (project setting).
  const suggestionsEnabled = project?.reply_suggestions_enabled !== false

  // Whenever the modal opens for a new ticket, fetch fresh suggestions —
  // unless the channel has them disabled.
  useEffect(() => {
    if (!ticket) return
    setDraft('')
    setCopiedIdx(null)
    if (!suggestionsEnabled) {
      setSuggestions([])
      setLoading(false)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setSuggestions([])
    ;(async () => {
      try {
        const { data, error: err } = await supabase.functions.invoke(
          'suggest-replies',
          { body: { ticket_id: ticket.id, locale: i18n.language } },
        )
        if (cancelled) return
        if (err) {
          let detail = err.message
          try {
            const ctx = (err as { context?: Response }).context
            const body =
              ctx && typeof ctx.json === 'function' ? await ctx.json() : null
            if (body?.error) detail = body.error
          } catch {
            /* keep generic */
          }
          setError(detail)
          return
        }
        const result = data as { suggestions?: string[]; error?: string }
        if (result.error) {
          setError(result.error)
          return
        }
        setSuggestions(result.suggestions ?? [])
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [ticket, suggestionsEnabled])

  // Load the card's conversation thread (its source messages, inbound +
  // outbound) for context, ordered chronologically.
  const sourceIds = ticket?.source_message_ids?.join(',') ?? ''
  useEffect(() => {
    if (!ticket || sourceIds === '') {
      setThread([])
      return
    }
    let cancelled = false
    supabase
      .from('messages')
      .select('*')
      .in('id', sourceIds.split(','))
      .order('received_at', { ascending: true })
      .then(({ data, error: err }) => {
        if (cancelled) return
        if (err) {
          console.error('reply thread load', err)
          return
        }
        setThread((data as Message[]) ?? [])
      })
    return () => {
      cancelled = true
    }
  }, [ticket, sourceIds])

  // Load the project's most recent INBOUND message timestamp — drives the
  // 24h customer-service window. Project-wide (not card-scoped): the window
  // applies between the WABA and the contact, regardless of which card the
  // last message landed on.
  const projectId = project?.id ?? null
  useEffect(() => {
    if (!projectId) {
      setLastInboundAt(null)
      return
    }
    let cancelled = false
    supabase
      .from('messages')
      .select('received_at')
      .eq('project_id', projectId)
      .eq('direction', 'inbound')
      .order('received_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return
        setLastInboundAt(
          data?.received_at ? new Date(data.received_at).getTime() : null,
        )
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  // Focus the composer when it appears so power users can start typing
  // immediately (no need to click).
  useEffect(() => {
    if (ticket && composerRef.current) composerRef.current.focus()
  }, [ticket])

  // ── Permission (mirrors the server check in d360-send) ───────────────
  const canReply = useMemo(() => {
    if (!ticket || !project) return false
    const isOwner = currentUserId === project.user_id
    const isAssignee =
      !!ticket.assignee_id && ticket.assignee_id === currentUserId
    if (isOwner || isAssignee) return true
    const perm = project.reply_permission ?? 'owner'
    if (perm === 'subscribers') return subscriberIds?.has(currentUserId) ?? false
    if (perm === 'all_members') return subscribableUserIds.includes(currentUserId)
    return false // 'owner'
  }, [ticket, project, currentUserId, subscriberIds, subscribableUserIds])

  // ── 24h window ───────────────────────────────────────────────────────
  // Null lastInboundAt = never received anything → can't open a chat.
  const windowExpired =
    lastInboundAt == null || Date.now() - lastInboundAt > WINDOW_MS

  async function copyToClipboard(text: string, idx: number | 'draft') {
    try {
      await navigator.clipboard.writeText(text)
      if (typeof idx === 'number') {
        setCopiedIdx(idx)
        window.setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 1600)
      } else {
        notify({
          title: t('reply.copiedToast'),
          message: t('reply.copiedToastMessage'),
          tone: 'success',
        })
      }
    } catch {
      notify({
        title: t('reply.copyFailedToast'),
        message: t('reply.copyFailedMessage'),
        tone: 'error',
      })
    }
  }

  async function sendReply() {
    if (!ticket || sending || !canReply || windowExpired) return
    const text = draft.trim()
    if (!text) return
    setSending(true)
    try {
      // Always quote the contact's latest inbound message so WhatsApp
      // threads the reply to it (same behaviour as the card detail).
      let latestInboundId: string | null = null
      for (const m of thread) if (m.direction === 'inbound') latestInboundId = m.id
      const { data, error: err } = await supabase.functions.invoke('d360-send', {
        body: {
          ticket_id: ticket.id,
          body: text,
          reply_to_message_id: latestInboundId,
        },
      })
      // Edge function errors (4xx/5xx) surface as `err` with the JSON body
      // reachable via err.context — pull the `code` for a precise message.
      if (err) {
        let code: string | null = null
        let detail = err.message
        try {
          const ctx = (err as { context?: Response }).context
          const b = ctx && typeof ctx.json === 'function' ? await ctx.json() : null
          if (b?.code) code = b.code
          if (b?.error) detail = b.error
        } catch {
          /* keep generic */
        }
        if (code === 'window_expired') {
          setLastInboundAt(0) // force the UI into the expired state
          notify({
            title: t('reply.windowExpired'),
            message: t('reply.windowExpiredHint'),
            tone: 'error',
          })
          return
        }
        if (code === 'not_allowed') {
          notify({
            title: t('reply.noPermission'),
            message: t('reply.noPermissionHint'),
            tone: 'error',
          })
          return
        }
        throw new Error(detail)
      }
      const result = data as { ok?: boolean; message?: Message; error?: string }
      if (!result.ok || !result.message) {
        throw new Error(result.error ?? 'send failed')
      }
      setThread((arr) => [...arr, result.message!])
      setDraft('')
      onReplySent?.(result.message)
    } catch (e) {
      notify({
        title: t('reply.sendErrorToast'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setSending(false)
    }
  }

  function previewOf(m: Message): string {
    if (m.body?.trim()) return m.body
    if (m.transcription?.trim()) return m.transcription
    switch (m.media_type) {
      case 'audio':
        return t('reply.mediaAudio')
      case 'image':
        return t('reply.mediaImage')
      case 'video':
        return t('reply.mediaVideo')
      case 'document':
        return t('reply.mediaDocument')
      default:
        return t('reply.mediaGeneric')
    }
  }

  if (!ticket) return null

  const sendDisabled = sending || !draft.trim() || !canReply || windowExpired

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--color-border-soft)] px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              <MessageCircle className="h-3.5 w-3.5" />
              {t('reply.title')}
            </div>
            <div className="mt-0.5 truncate text-[15px] font-semibold text-[var(--color-text-strong)]">
              {ticket.title}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — conversation thread + suggestions + composer */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
          {/* Conversation thread */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              <MessageCircle className="h-3.5 w-3.5" />
              {t('reply.thread')}
            </div>
            {thread.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-[12px] text-[var(--color-text-dim)]">
                {t('reply.emptyThread')}
              </div>
            ) : (
              <div className="space-y-2">
                {thread.map((m) => {
                  const isOutbound = m.direction === 'outbound'
                  const author = m.author_id
                    ? profilesById[m.author_id]
                    : undefined
                  const authorName =
                    m.author_id === currentUserId
                      ? t('reply.you')
                      : profileName(author) || ''
                  return (
                    <div
                      key={m.id}
                      className={cn(
                        'flex',
                        isOutbound ? 'justify-end' : 'justify-start',
                      )}
                    >
                      <div
                        className={cn(
                          'max-w-[78%] rounded-2xl px-3 py-2 text-[12.5px] leading-relaxed',
                          isOutbound
                            ? 'bg-[var(--color-accent)] text-white'
                            : 'bg-[var(--color-surface-2)] text-[var(--color-text)]',
                        )}
                      >
                        <div className="whitespace-pre-wrap break-words">
                          {previewOf(m)}
                        </div>
                        <div
                          className={cn(
                            'mt-1 text-[10px]',
                            isOutbound
                              ? 'text-white/70'
                              : 'text-[var(--color-text-dim)]',
                          )}
                        >
                          {isOutbound && authorName
                            ? `${t('reply.sentBy', { name: authorName })} · ${formatRelative(m.received_at)}`
                            : formatRelative(m.received_at)}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* AI suggestions — hidden when disabled for this channel. */}
          {suggestionsEnabled && (
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              <Sparkles className="h-3.5 w-3.5" />
              {t('reply.aiSuggestions')}
            </div>
            {loading && (
              <div className="flex items-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] px-3 py-4 text-[12.5px] text-[var(--color-text-dim)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('reply.generating')}
              </div>
            )}
            {error && !loading && (
              <div className="rounded-xl border border-[var(--color-danger)]/30 bg-[var(--color-danger-bg)] px-3 py-2.5 text-[12px] text-[var(--color-danger)]">
                {t('reply.error', { error })}
              </div>
            )}
            {!loading && !error && suggestions.length === 0 && (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-[12px] text-[var(--color-text-dim)]">
                {t('reply.noSuggestions')}
              </div>
            )}
            {!loading && suggestions.length > 0 && (
              <div className="space-y-2">
                {suggestions.map((s, i) => (
                  <div
                    key={i}
                    className="group flex items-start gap-2 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-3 py-2.5 transition-colors hover:border-[var(--color-border)]"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(s)
                        composerRef.current?.focus()
                      }}
                      className="flex-1 text-left text-[12.5px] leading-relaxed text-[var(--color-text)] transition-colors hover:text-[var(--color-text-strong)]"
                      title={t('reply.useSuggestionTooltip')}
                    >
                      {s}
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(s, i)}
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium transition-colors',
                        copiedIdx === i
                          ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                          : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
                      )}
                      title={t('reply.copyTooltip')}
                    >
                      {copiedIdx === i ? (
                        <>
                          <Check className="h-3 w-3" />
                          {t('reply.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" />
                          {t('reply.copy')}
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Free-text composer */}
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              {t('reply.yourReply')}
            </div>
            <textarea
              ref={composerRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  void sendReply()
                }
              }}
              placeholder={t('reply.composerPlaceholder')}
              rows={4}
              disabled={!canReply || windowExpired}
              className="block w-full resize-y rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-[13px] leading-relaxed text-[var(--color-text-strong)] outline-none transition-colors focus:border-[var(--color-link)] disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
        </div>

        {/* Footer — real send (gated by permission + 24h window) */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)] px-6 py-3">
          {/* Left: gating status */}
          <div className="min-w-0 flex-1">
            {!canReply ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-dim)]">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate" title={t('reply.noPermissionHint')}>
                  {t('reply.noPermission')}
                </span>
              </span>
            ) : windowExpired ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-danger)]">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate" title={t('reply.windowExpiredHint')}>
                  {t('reply.windowExpired')}
                </span>
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-3 py-1.5 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
            >
              {t('reply.cancel')}
            </button>
            <button
              type="button"
              onClick={sendReply}
              disabled={sendDisabled}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              {sending ? t('reply.sending') : t('reply.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
